import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full h-12 px-4 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C87A4B]/40 focus:border-[#C87A4B] transition-all";

export default function Contact() {
  const [form, setForm] = useState({ name: "", company: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please complete all required fields.");
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. Our team will respond shortly.");
      setForm({ name: "", company: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#FAF8F5] min-h-screen">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-36 pb-24 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C87A4B] mb-4">Contact</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-[#0B111E] mb-6">Talk to our team.</h1>
          <p className="text-slate-600 leading-relaxed mb-10 max-w-md">
            Large documents, multilingual programmes, regulatory projects or ongoing requirements —
            tell us about your project and we will prepare a tailored solution.
          </p>
          <div className="flex flex-col gap-4 text-sm text-slate-600">
            <span className="flex items-center gap-3"><Mail className="w-4 h-4 text-[#C87A4B]" /> hello@translateit.website</span>
            <span className="flex items-center gap-3"><MapPin className="w-4 h-4 text-[#C87A4B]" /> London, United Kingdom</span>
          </div>
        </motion.div>
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
          data-testid="contact-form"
          className="bg-white rounded-2xl border border-slate-200 shadow-[0_20px_50px_rgba(11,17,30,0.08)] p-8 flex flex-col gap-4"
        >
          <input data-testid="contact-name" placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
          <input data-testid="contact-company" placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputCls} />
          <input data-testid="contact-email" type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
          <textarea data-testid="contact-message" placeholder="Tell us about your project *" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
            className={`${inputCls} h-36 py-3 resize-none`} />
          <button type="submit" disabled={busy} data-testid="contact-submit"
            className="h-12 rounded-lg bg-[#C87A4B] hover:bg-[#B3673A] disabled:opacity-60 text-white font-semibold flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send Message <ArrowRight className="w-4 h-4" /></>}
          </button>
        </motion.form>
      </main>
      <Footer />
    </div>
  );
}
